import { forwardRef } from 'react';
import { DataTableFilterRef, FilterProps } from '../DataTableTypes';
import { DataTableHeaderBooleanFilter } from './DataTableHeaderBooleanFilter';
import { DataTableHeaderDateFilter } from './DataTableHeaderDateFilter';
import { DataTableHeaderNumberFilter } from './DataTableHeaderNumberFilter';
import { DataTableHeaderOwnerFilter } from './DataTableHeaderOwnerFilter';
import { DataTableHeaderRecordSearchFilter } from './DataTableHeaderRecordSearchFilter';
import { DataTableHeaderSelectionFilter } from './DataTableHeaderSelectionFilter';
import { DataTableHeaderSetFilter } from './DataTableHeaderSetFilter';
import { DataTableHeaderTextFilter } from './DataTableHeaderTextFilter';

/**
 * Filter component that is displayed in the header of the table
 * and renders proper filter component based on the column meta
 *
 * Values are configured here
 * {@link frontend/src/global.d.ts}
 *
 * To add a new filter variant, update ALL of the following:
 *   1. `ColumnMeta.filterVariant` union in frontend/src/global.d.ts
 *      (plus any variant-specific meta config, e.g. `recordSearch`).
 *   2. Add a filter fn in ./dataTableFilterFns.ts.
 *   3. Wire the variant → filter fn in `getColHeaderFilters` in
 *      ../tableUtilsNew.tsx.
 *   4. Add a case in `transformFiltersForServer` in ../tableUtilsNew.tsx
 *      mapping the internal value shape to FilterType for the API.
 *   5. Create the header popover component (this directory) implementing
 *      `DataTableFilterRef` so `resetFilters()` / `setFilterValue()` work
 *      when the user clicks the pill close button or "Reset All".
 *   6. Register the new component in the switch below.
 *   7. Add a case in `DataTableActiveFilters.tsx` so the applied-filter
 *      pill renders a readable label (otherwise the pill bar stays hidden).
 *   8. Add tests covering the filter fn and server transform in
 *      ../__tests__ and ./__tests__.
 */
export const DateTableHeaderFilter = forwardRef<
  DataTableFilterRef,
  FilterProps<any>
>((props, ref) => {
  const { column } = props;
  const { filterVariant } = column.columnDef.meta ?? {};

  switch (filterVariant) {
    case 'BOOLEAN':
      return <DataTableHeaderBooleanFilter ref={ref} {...props} />;
    case 'DATE':
      return <DataTableHeaderDateFilter ref={ref} {...props} />;
    case 'NUMBER':
      return <DataTableHeaderNumberFilter ref={ref} {...props} />;
    case 'OWNER':
      return <DataTableHeaderOwnerFilter ref={ref} {...props} />;
    case 'RECORD_SEARCH':
      return <DataTableHeaderRecordSearchFilter ref={ref} {...props} />;
    case 'SET':
      return <DataTableHeaderSetFilter ref={ref} {...props} />;
    case 'SELECTION':
      return <DataTableHeaderSelectionFilter ref={ref} {...props} />;
    case 'TEXT':
      return <DataTableHeaderTextFilter ref={ref} {...props} />;
    default:
      return null;
  }
});
